import React, { type ReactElement, useState } from "react";
import { PaymentMethod } from "../../../common/enums";
import { WorkflowMode } from "../common/enums";
import {
  Arena,
  DetailedPaymentPrefUnionObjectType,
  RedactedPaymentPref,
  Position,
  type OtherTileConfig,
} from "../common/types";
import { type PaymentPermissionsByArena } from "../Containers/PaymentPrefForPositionContainer/types";
import { ConsolidatedPaymentPrefContextProvider } from "../Context";
import { type AssociatePaymentPrefMutationTypeEnum } from "../Context/enums";
import {
  type AccountEdge,
  type AccountPositions,
  type ExistingPaymentPref,
  type PaymentPrefCurrentUser,
  type RailsQueryFilters,
  type SelectedBankCountry,
} from "../Context/types";
import { type AvailablePaymentRailsResponse } from "../hooks/useAvailablePaymentRails";

// TODO Add robust documentation
export interface ConsolidatedPaymentPrefProviderProps {
  /**
   * These are existing payment prefs on the account. Used to display a list to choose from. Required for the create flow
   */
  existingPaymentPrefs?: ExistingPaymentPref[];

  /**
   * Required only for the create flow
   */
  availablePaymentMethods?: Record<PaymentMethod, { isAvailable: boolean; warning: string }>;

  singleAvailablePaymentMethod?: PaymentMethod;

  accounts?: AccountEdge[];

  /** Required only on GP flows and when has_position_payment_pref_staff_approvals arena FF is enabled
   */
  accountName?: string;

  /**
   * The arena in context of this workflow.
   */
  arena?: Arena;

  /**
   * Only required for the position flows
   */
  currentPosition?: Position;

  /**
   * The initial mutation (through selection of a enum) to associate payment preferences with target objects.
   * The association mutation can also be set later in the workflow, dynamically.
   */
  initialAssociatePaymentPref?: AssociatePaymentPrefMutationTypeEnum;

  /**
   * The initial payment method selected for an existing payment preference.
   * Only used when the existing payment preference is being edited.
   */
  initialSelectedPaymentMethod?: PaymentMethod;

  /**
   * Initial data for editing an existing payment preference.
   * Only used when the existing payment preference is being edited.
   */
  initialPaymentPrefId?: string;

  /**
   * The source payment pref id for a switch operation
   */
  sourcePaymentPrefId?: string;

  children: ReactElement;
  ListOfPositionsToUpdateComponent?: React.FC | null;

  /**
   * The current mode in which the workflow is operating.
   */
  workflowMode: WorkflowMode;

  /**
   * Initial Header component to render on update payment method page
   */
  initialSelectAvailablePaymentPrefsFormHeaderComponent?: React.FC | null;

  /**
   * The initial payment pref detail to display. Only used during payee workflow
   */
  initialPaymentPrefDetail?: DetailedPaymentPrefUnionObjectType;

  /**
   * Filters for the availablePaymentRails query. Set by each workflow so that
   * SelectPaymentMethodV2 does not have to infer the workflow type.
   */
  railsQueryFilters?: RailsQueryFilters;

  /**
   * Payment permissions keyed by arena ID.
   */
  paymentPermissionsByArena?: PaymentPermissionsByArena;

  /**
   * The current user's server-gated security signal used to route LP-initiated changes.
   */
  currentUser?: PaymentPrefCurrentUser;

  /**
   * Tile/form dressing for the "Other" payment method. Only consumed when "Other" is
   * present in availablePaymentMethods.
   */
  otherTileConfig?: OtherTileConfig;
}

export const ConsolidatedPaymentPrefProvider = ({
  existingPaymentPrefs,
  availablePaymentMethods,
  singleAvailablePaymentMethod,
  accounts,
  currentPosition,
  ListOfPositionsToUpdateComponent: DefaultListOfPositionsToUpdateComponent = null,
  initialAssociatePaymentPref,
  initialSelectedPaymentMethod,
  initialPaymentPrefId,
  sourcePaymentPrefId,
  workflowMode,
  initialSelectAvailablePaymentPrefsFormHeaderComponent = null,
  accountName,
  arena,
  initialPaymentPrefDetail,
  railsQueryFilters,
  paymentPermissionsByArena,
  currentUser,
  otherTileConfig,
  children,
}: ConsolidatedPaymentPrefProviderProps) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>(
    singleAvailablePaymentMethod ?? initialSelectedPaymentMethod,
  );
  const [paymentPrefDetail, setPaymentPrefDetail] = useState<
    DetailedPaymentPrefUnionObjectType | undefined
  >(initialPaymentPrefDetail);
  const [selectedPaymentPref, setSelectedPaymentPref] = useState<
    RedactedPaymentPref | undefined
  >();
  const [isExistingPaymentPref, setIsExistingPaymentPref] = useState<boolean>(false);
  const [paymentPrefId, setPaymentPrefId] = useState<string | undefined>(initialPaymentPrefId);
  const [updatedPositionsPerAccount, setUpdatedPositionsPerAccount] = useState<
    AccountPositions | undefined
  >();
  const [associatePaymentPref, setAssociatePaymentPref] = useState<
    AssociatePaymentPrefMutationTypeEnum | undefined
  >(initialAssociatePaymentPref);

  const [
    SelectAvailablePaymentPrefsFormHeaderComponent,
    setSelectAvailablePaymentPrefsFormHeaderComponent,
  ] = useState<React.FC | null>(() => initialSelectAvailablePaymentPrefsFormHeaderComponent);
  const [ListOfPositionsToUpdateComponent, setListOfPositionsToUpdateComponent] =
    useState<React.FC | null>(() => DefaultListOfPositionsToUpdateComponent);
  const [requestReason, setRequestReason] = useState<string>("");
  const [approvalCompleted, setApprovalCompleted] = useState<boolean>(false);
  const [selectedBankCountry, setSelectedBankCountry] = useState<
    SelectedBankCountry | undefined
  >();
  const [bankCountryLocked, setBankCountryLocked] = useState<boolean>(false);
  const [availablePaymentRailsResponse, setAvailablePaymentRailsResponse] = useState<
    AvailablePaymentRailsResponse | undefined
  >();
  const [otherPaymentPrefInstructions, setOtherPaymentPrefInstructions] = useState<
    string | undefined
  >();

  return (
    <ConsolidatedPaymentPrefContextProvider
      value={{
        associatePaymentPref,
        setAssociatePaymentPref,
        SelectAvailablePaymentPrefsFormHeaderComponent,
        setSelectAvailablePaymentPrefsFormHeaderComponent,
        ListOfPositionsToUpdateComponent,
        setListOfPositionsToUpdateComponent,
        existingPaymentPrefs,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        paymentPrefDetail,
        setPaymentPrefDetail,
        selectedPaymentPref,
        setSelectedPaymentPref,
        isExistingPaymentPref,
        setIsExistingPaymentPref,
        sourcePaymentPrefId,
        paymentPrefId,
        setPaymentPrefId,
        updatedPositionsPerAccount,
        setUpdatedPositionsPerAccount,
        currentPosition,
        accounts,
        paymentMethods: availablePaymentMethods,
        singleAvailablePaymentMethod,
        workflowMode,
        accountName,
        arena,
        initialPaymentPrefDetail,
        railsQueryFilters,
        paymentPermissionsByArena,
        currentUser,
        requestReason,
        setRequestReason,
        approvalCompleted,
        setApprovalCompleted,
        selectedBankCountry,
        setSelectedBankCountry,
        bankCountryLocked,
        setBankCountryLocked,
        availablePaymentRailsResponse,
        setAvailablePaymentRailsResponse,
        otherTileConfig,
        otherPaymentPrefInstructions,
        setOtherPaymentPrefInstructions,
      }}
    >
      {children}
    </ConsolidatedPaymentPrefContextProvider>
  );
};
